import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { isAuthenticated, getCurrentUsername } from '../api/prismAPI';

/**
 * ProtectedRoute - Wraps routes that require authentication and specific roles
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The component to render if authorized
 * @param {string|string[]} props.allowedRoles - Role(s) that can access this route
 * @param {string} props.userRole - Current user's role from parent
 * 
 * Usage:
 * <Route path="/coaching" element={
 *   <ProtectedRoute allowedRoles={["team_lead", "platform_admin"]} userRole={userRole}>
 *     <CoachingDashboard />
 *   </ProtectedRoute>
 * } />
 */
const ProtectedRoute = ({ children, allowedRoles, userRole }) => {
  const location = useLocation();
  
  // Check if user is authenticated
  if (!isAuthenticated()) {
    // Redirect to login, save attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  // If no role check needed, allow access
  if (!allowedRoles) {
    return children;
  }
  
  // Normalize allowedRoles to array
  const rolesArray = Array.isArray(allowedRoles) ? allowedRoles : [allowedRoles];
  
  // Check if user's role is allowed
  if (!userRole || !rolesArray.includes(userRole)) {
    // Redirect to unauthorized page or their default dashboard
    return <Navigate to="/unauthorized" replace />;
  }
  
  // User is authenticated and has required role
  return children;
};

export default ProtectedRoute;
